import { IBannerInlinePlaceholderProps } from '@tekface/smart-activation';
import { SearchProductFilter } from '@teko/discovery/v2';
import {
  BreadcrumbProps,
  Breadcrumbs,
  Container,
  Flex,
  Shape,
  styled,
} from '@teko/ds-desktop';
import { HomepageFilled } from '@teko/ds-icon';
import { Description } from '@teko/teko-ecommerce';
import { ProductCardProps } from '@teko/types/ecommerce';
import { HeadSection } from 'components/HeadSection';
import { RelateVideosAndPosts } from 'components/RelatedVideosAndPosts';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Breadcrumb, FeaturedProductsProps, SeoInfo } from '../shared';
import { ProductListWithFilter } from './ProductListWithFilter';

const FAQSection = dynamic(() =>
  import('components/FAQ').then((mod) => mod.FAQSection),
);

export interface SearchPageProps {
  serverProducts?: ProductCardProps[];
  serverPagination?: {
    totalItems: number;
    totalPages: number;
  };
  seoInfo?: SeoInfo;
  serverFilters?: SearchProductFilter;
  filterPrice?: {
    max: number;
    min: number;
  };
  breadcrumbs?: Breadcrumb;
  forceNoIndex?: boolean;
  featuredProducts?: FeaturedProductsProps;
  inlineBanners?: IBannerInlinePlaceholderProps<{}>['banners'];
}

export const SearchPage: NextPage<SearchPageProps> = (props) => {
  const {
    breadcrumbs,
    seoInfo,
    forceNoIndex,
    serverProducts,
    serverFilters,
    filterPrice,
    serverPagination,
    featuredProducts,
    inlineBanners,
  } = props;
  const router = useRouter();
  const { query, asPath } = router;
  const { page } = query;
  const {
    epicOmni976,
    addSchemaBreadcrumbList,
    baseDomain,
    epicEcom495,
    isPaginalSearchPage,
  } = useTenantConfig();

  // display breadcrumbs for collection page
  const breadCrumbs: BreadcrumbProps[] = [
    ...(!asPath.startsWith('/search')
      ? epicEcom495
        ? [
            {
              link: '/',
              name: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <HomepageFilled
                    size={16}
                    color="placeholder"
                    style={{ marginRight: '4px' }}
                  />
                  {t('Trang chủ')}
                </div>
              ),
            },
          ]
        : [
            {
              name: <HomepageFilled size={24} color="placeholder" />,
              link: '/',
            },
          ]
      : []),
    ...((epicOmni976 || epicEcom495) && breadcrumbs && breadcrumbs.length
      ? breadcrumbs
      : []),
    ...(seoInfo?.siteTitle
      ? [
          {
            disabled: true,
            link: '#',
            name: seoInfo?.siteTitle,
            isEndOfBread: true,
          },
        ]
      : []),
  ];

  const isShowRelatedPostsAndVideos =
    !!seoInfo?.relatedVideos?.urls?.length ||
    !!seoInfo?.relatedPosts?.urls?.length;

  const isShowSiteDescription =
    isShowRelatedPostsAndVideos || (seoInfo?.siteDescription && !page);

  const metaTitle = seoInfo?.metaTitle || seoInfo?.siteTitle;
  const paginationMetaTitle =
    isPaginalSearchPage && !!page ? `${page} - ${metaTitle}` : metaTitle;

  return (
    <>
      {!!addSchemaBreadcrumbList && !!breadCrumbs?.length && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadCrumbs.map(({ name, link }, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    name:
                      typeof name === 'string'
                        ? name
                        : !index && t('Trang chủ'),
                    '@id':
                      index === breadCrumbs.length - 1 || link === '#'
                        ? `${baseDomain}${asPath?.split('?')[0]}`
                        : `${baseDomain}${link}`,
                  },
                })),
              }),
            }}
          />
        </Head>
      )}
      <HeadSection
        {...seoInfo}
        noindex={forceNoIndex || !seoInfo?.canonical}
        title={paginationMetaTitle}
        metaTitle={paginationMetaTitle}
        canonical={
          isPaginalSearchPage && !!page ? undefined : seoInfo?.canonical
        }
        links={[
          seoInfo?.siteDescription
            ? {
                rel: 'stylesheet',
                href: 'https://shopfront-cdn.tekoapis.com/static/stylesheet/ckeditor.css',
              }
            : {},
        ]}
        image={seoInfo?.thumbnailUrl}
      />
      {breadCrumbs && (
        <Container>
          <Breadcrumbs breadcrumbs={breadCrumbs} paddingLeft="s16" />
        </Container>
      )}

      <ProductListWithFilter
        marginTop={breadCrumbs ? undefined : 's16'}
        serverProducts={serverProducts}
        serverPagination={serverPagination}
        serverFilters={serverFilters}
        filterPrice={filterPrice}
        siteTitle={seoInfo?.siteTitle}
        serverSeoInfo={seoInfo}
        featuredProducts={featuredProducts}
        inlineBanners={inlineBanners}
      />
      {!isPaginalSearchPage && !page && (
        <>
          {(isShowRelatedPostsAndVideos || isShowSiteDescription) && (
            <Container>
              <Flex gap="s16" align="initial">
                {isShowSiteDescription && (
                  <Shape
                    width={
                      isShowRelatedPostsAndVideos
                        ? 'calc((100% - 16px)/3 * 2)'
                        : '100%'
                    }
                    marginTop="s12"
                  >
                    <StyledDescription
                      className="ck-content"
                      content={seoInfo?.siteDescription || 'Đang cập nhật...'}
                      maxHeight={760}
                      showMoreLabel={t('Xem thêm nội dung')}
                      showLessLabel={t('Thu gọn nội dung')}
                    />
                  </Shape>
                )}
                {isShowRelatedPostsAndVideos && (
                  <Shape width="calc((100% - 16px)/3)">
                    <RelateVideosAndPosts
                      relatedVideos={seoInfo?.relatedVideos}
                      relatedPosts={seoInfo?.relatedPosts}
                    />
                  </Shape>
                )}
              </Flex>
            </Container>
          )}
          {!!query?.slug && !!seoInfo?.faq && seoInfo.faq.length > 0 ? (
            <FAQSection faq={seoInfo.faq} />
          ) : null}
        </>
      )}
    </>
  );
};

const StyledDescription = styled(Description)`
  background-color: white;
  margin-bottom: 1rem;
  border-radius: 1rem;
  line-height: 1.5rem;
  .table table {
    max-width: 100%;
    table-layout: auto;
  }
  .ck-content .table table td,
  .ck-content .table table th {
    min-width: 2em;
    padding: 0.4em;
    border: 1px solid #bfbfbf;
  }
  ul,
  ul li {
    list-style-type: inherit;
  }

  ul {
    /* Default user agent stylesheet. */
    padding-left: 40px;
  }

  ol,
  ol li {
    list-style-type: decimal;
  }
  ol {
    /* Default user agent stylesheet, you can change it to your needs. */
    padding-left: 40px;
  }

  h2 {
    font-size: 20px;
    line-height: 1.4;
  }
  h3 {
    font-size: 16px;
    line-height: 1.3;
  }
  h4 {
    font-size: 14px;
  }
  h5 {
    font-size: 12px;
  }
  h6 {
    font-size: 10px;
  }
`;
